/**
 * Step 01 — Profile fields.
 *
 * Rich field definitions with options and descriptions,
 * matching the dating-schema profile-fields pattern.
 */

import type { FieldGroup } from './types.js';

// ─── Shared option arrays (exported for reuse) ─────────────────────────────

/**
 * Gender options — matches dating-schema (01-hero-identity.ts) EXACTLY.
 * Keep in sync with dating so both products share the same enum + copy.
 */
export const genderOptions = [
  { value: 'man', label: 'Man', description: 'Identify as male' },
  { value: 'woman', label: 'Woman', description: 'Identify as female' },
  {
    value: 'non_binary',
    label: 'Non-binary',
    description: 'Neither exclusively male nor female',
  },
  {
    value: 'trans_man',
    label: 'Trans Man',
    description: 'Assigned female at birth, identify as male',
  },
  {
    value: 'trans_woman',
    label: 'Trans Woman',
    description: 'Assigned male at birth, identify as female',
  },
  { value: 'genderfluid', label: 'Genderfluid', description: 'Gender identity shifts over time' },
  {
    value: 'genderqueer',
    label: 'Genderqueer',
    description: 'Outside the traditional gender binary',
  },
  { value: 'agender', label: 'Agender', description: 'No gender identity or gender-neutral' },
  {
    value: 'bigender',
    label: 'Bigender',
    description: 'Identify as two genders simultaneously or alternating',
  },
  {
    value: 'pangender',
    label: 'Pangender',
    description: 'Identify with all genders or many gender identities',
  },
  {
    value: 'androgynous',
    label: 'Androgynous',
    description: 'Blend of masculine and feminine expression',
  },
  {
    value: 'intersex',
    label: 'Intersex',
    description: "Born with sex characteristics that don't fit typical male/female",
  },
  {
    value: 'gender_nonconforming',
    label: 'Gender Non-conforming',
    description: "Expression doesn't align with conventional expectations",
  },
  {
    value: 'two_spirit',
    label: 'Two-Spirit',
    description: 'Indigenous identity encompassing multiple genders',
  },
  {
    value: 'hijra',
    label: 'Hijra',
    description: 'South Asian third-gender identity — legally recognized in India',
  },
  { value: 'other', label: 'Other', description: 'Another identity not listed here' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say', description: 'Keep this private' },
] as const;

export const religionOptions = [
  { value: 'hindu', label: 'Hindu', description: 'Sanatana Dharma traditions' },
  { value: 'muslim_sunni', label: 'Muslim (Sunni)', description: 'Largest branch of Islam' },
  { value: 'muslim_shia', label: 'Muslim (Shia)', description: 'Second-largest branch of Islam' },
  { value: 'muslim_sufi', label: 'Muslim (Sufi)', description: 'Mystical Islamic tradition' },
  { value: 'muslim_other', label: 'Muslim (Other)', description: 'Ahmadiyya or other traditions' },
  {
    value: 'christian_catholic',
    label: 'Christian (Catholic)',
    description: 'Roman Catholic tradition',
  },
  {
    value: 'christian_protestant',
    label: 'Christian (Protestant)',
    description: 'Baptist, Methodist, Pentecostal, etc.',
  },
  {
    value: 'christian_other',
    label: 'Christian (Other)',
    description: 'Orthodox, Syrian, or other traditions',
  },
  { value: 'sikh', label: 'Sikh', description: 'Founded by Guru Nanak' },
  { value: 'jain_digambar', label: 'Jain (Digambar)', description: 'Sky-clad Jain tradition' },
  {
    value: 'jain_shwetambar',
    label: 'Jain (Shwetambar)',
    description: 'White-clad Jain tradition',
  },
  { value: 'buddhist', label: 'Buddhist', description: "Followers of the Buddha's teachings" },
  {
    value: 'parsi_zoroastrian',
    label: 'Parsi / Zoroastrian',
    description: 'Ancient Persian faith',
  },
  { value: 'jewish', label: 'Jewish', description: 'Judaism — Abrahamic faith' },
  { value: 'bahai', label: "Baha'i", description: 'Unity of all religions and humanity' },
  {
    value: 'spiritual',
    label: 'Spiritual but not religious',
    description: 'Personal spiritual practice',
  },
  { value: 'atheist', label: 'Atheist', description: 'No belief in deities' },
  { value: 'agnostic', label: 'Agnostic', description: 'Uncertain about higher power' },
  { value: 'no_religion', label: 'No Religion', description: 'Simply non-religious' },
  {
    value: 'inter_religion',
    label: 'Inter-Religion / Multi-faith',
    description: 'Multi-faith background',
  },
  { value: 'other', label: 'Other', description: 'A faith not listed above' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const languageOptions = [
  { value: 'hindi', label: 'Hindi (हिन्दी)' },
  { value: 'english', label: 'English' },
  { value: 'bengali', label: 'Bengali (বাংলা)' },
  { value: 'telugu', label: 'Telugu (తెలుగు)' },
  { value: 'marathi', label: 'Marathi (मराठी)' },
  { value: 'tamil', label: 'Tamil (தமிழ்)' },
  { value: 'urdu', label: 'Urdu (اردو)' },
  { value: 'gujarati', label: 'Gujarati (ગુજરાતી)' },
  { value: 'kannada', label: 'Kannada (ಕನ್ನಡ)' },
  { value: 'odia', label: 'Odia (ଓଡ଼ିଆ)' },
  { value: 'malayalam', label: 'Malayalam (മലയാളം)' },
  { value: 'punjabi', label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { value: 'assamese', label: 'Assamese (অসমীয়া)' },
  { value: 'maithili', label: 'Maithili (मैथिली)' },
  { value: 'bhojpuri', label: 'Bhojpuri (भोजपुरी)' },
  { value: 'rajasthani', label: 'Rajasthani (राजस्थानी)' },
  { value: 'chhattisgarhi', label: 'Chhattisgarhi (छत्तीसगढ़ी)' },
  { value: 'haryanvi', label: 'Haryanvi (हरियाणवी)' },
  { value: 'tulu', label: 'Tulu (ತುಳು)' },
  { value: 'sanskrit', label: 'Sanskrit (संस्कृतम्)' },
  { value: 'sindhi', label: 'Sindhi (سنڌي)' },
  { value: 'kashmiri', label: 'Kashmiri (कॉशुर)' },
  { value: 'nepali', label: 'Nepali (नेपाली)' },
  { value: 'konkani', label: 'Konkani (कोंकणी)' },
  { value: 'dogri', label: 'Dogri (डोगरी)' },
  { value: 'manipuri', label: 'Manipuri (মৈতৈলোন্)' },
  { value: 'bodo', label: 'Bodo (बड़ो)' },
  { value: 'santali', label: 'Santali (ᱥᱟᱱᱛᱟᱲᱤ)' },
  // International languages
  { value: 'spanish', label: 'Spanish (Español)' },
  { value: 'french', label: 'French (Français)' },
  { value: 'german', label: 'German (Deutsch)' },
  { value: 'mandarin', label: 'Mandarin (普通话)' },
  { value: 'japanese', label: 'Japanese (日本語)' },
  { value: 'korean', label: 'Korean (한국어)' },
  { value: 'arabic', label: 'Arabic (العربية)' },
  { value: 'portuguese', label: 'Portuguese (Português)' },
  { value: 'russian', label: 'Russian (Русский)' },
  { value: 'italian', label: 'Italian (Italiano)' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Reservation / caste category — exhaustive list per Government of India
 * and state-level backward-class classifications.
 *
 * References:
 *  - Articles 341 / 342 (SC / ST)
 *  - Mandal Commission + 77th Amendment (OBC)
 *  - 103rd Amendment, 2019 (EWS)
 *  - State lists: MBC (Tamil Nadu, Bihar), SEBC (Maharashtra, Gujarat, etc.),
 *    SBC (Karnataka), DNT / NT / VJNT (Maharashtra, MP, and others)
 */
export const reservationOptions = [
  {
    value: 'SC',
    label: 'Scheduled Caste (SC)',
    description: 'Listed in the Presidential Order under Article 341',
  },
  {
    value: 'ST',
    label: 'Scheduled Tribe (ST)',
    description: 'Listed in the Presidential Order under Article 342',
  },
  {
    value: 'OBC_NCL',
    label: 'OBC — Non-Creamy Layer',
    description: 'Other Backward Class, eligible for reservation',
  },
  {
    value: 'OBC_CL',
    label: 'OBC — Creamy Layer',
    description: 'Other Backward Class, income above the creamy-layer threshold',
  },
  {
    value: 'MBC',
    label: 'Most Backward Class (MBC)',
    description: 'State sub-category of OBC (e.g., Tamil Nadu, Bihar)',
  },
  {
    value: 'SEBC',
    label: 'Socially & Educationally Backward Class (SEBC)',
    description: 'State-notified SEBC (e.g., Maharashtra, Gujarat)',
  },
  {
    value: 'SBC',
    label: 'Special Backward Class (SBC)',
    description: 'State-notified special backward category',
  },
  {
    value: 'DNT_NT',
    label: 'Denotified / Nomadic Tribes (DNT / NT / VJNT)',
    description: 'Denotified, Nomadic, and Semi-Nomadic communities',
  },
  {
    value: 'EWS',
    label: 'Economically Weaker Section (EWS)',
    description: '10% reservation for unreserved categories (103rd Amendment)',
  },
  { value: 'GENERAL', label: 'General / Unreserved', description: 'No reservation category' },
  { value: 'DONT_KNOW', label: "Don't know", description: 'Not sure of the category' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say', description: 'Keep this private' },
] as const;

/**
 * Minority status — per Ministry of Minority Affairs, Government of India.
 * 6 religious minorities are centrally notified. Linguistic minorities are
 * defined at the state level (mother tongue ≠ majority language of the state).
 */
export const minorityOptions = [
  { value: 'NO', label: 'No', description: 'Not a minority community' },
  {
    value: 'MUSLIM',
    label: 'Religious Minority — Muslim',
    description: 'Centrally notified religious minority',
  },
  {
    value: 'CHRISTIAN',
    label: 'Religious Minority — Christian',
    description: 'Centrally notified religious minority',
  },
  {
    value: 'SIKH',
    label: 'Religious Minority — Sikh',
    description: 'Centrally notified religious minority',
  },
  {
    value: 'BUDDHIST',
    label: 'Religious Minority — Buddhist',
    description: 'Centrally notified religious minority',
  },
  {
    value: 'JAIN',
    label: 'Religious Minority — Jain',
    description: 'Centrally notified religious minority (2014)',
  },
  {
    value: 'PARSI',
    label: 'Religious Minority — Parsi / Zoroastrian',
    description: 'Centrally notified religious minority',
  },
  {
    value: 'LINGUISTIC',
    label: 'Linguistic Minority',
    description: 'Mother tongue is not the majority language of your state',
  },
  {
    value: 'BOTH',
    label: 'Both religious and linguistic minority',
    description: 'Belongs to a religious minority AND a linguistic minority',
  },
  { value: 'NOT_SURE', label: 'Not sure', description: "Don't know if you're a minority" },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say', description: 'Keep this private' },
] as const;

// ─── Field Group ────────────────────────────────────────────────────────────

export const profile: FieldGroup = {
  key: 'profile',
  label: 'Profile',
  description: 'Basic personal information — name, gender, date of birth, language, and religion.',
  step: 1,
  fields: [
    {
      order: 1,
      key: 'fullName',
      label: 'Full Name',
      type: 'object',
      step: 1,
      isOnboarding: true,
      description: 'First, middle, and last name as per Aadhaar or other govt ID',
    },
    {
      order: 4,
      key: 'gender',
      label: 'Gender',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      options: [...genderOptions],
    },
    {
      order: 5,
      key: 'dateOfBirth',
      label: 'Date of Birth',
      type: 'date',
      step: 1,
      isOnboarding: true,
    },
    {
      order: 6,
      key: 'whatsappNumber',
      label: 'WhatsApp Number',
      type: 'text',
      step: 1,
      isOnboarding: true,
      optional: true,
      description: 'Include country code',
    },
    {
      order: 7,
      key: 'primaryLanguage',
      label: 'Primary Language',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      options: [...languageOptions],
    },
    {
      order: 8,
      key: 'secondaryLanguages',
      label: 'Secondary Languages',
      type: 'multi_select',
      step: 1,
      isOnboarding: true,
      optional: true,
      options: [...languageOptions],
      maxSelect: 10,
    },
    {
      order: 9,
      key: 'familyReligion',
      label: 'Family Religion',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      options: [...religionOptions],
    },
    {
      order: 10,
      key: 'personalReligion',
      label: 'Personal Religion',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      optional: true,
      options: [...religionOptions],
    },
    {
      order: 11,
      key: 'reservationStatus',
      label: 'Reservation Category',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      options: [...reservationOptions],
      sensitive: true,
    },
    {
      order: 12,
      key: 'minorityStatus',
      label: 'Minority Status',
      type: 'single_select',
      step: 1,
      isOnboarding: true,
      options: [...minorityOptions],
    },
  ],
};
